import ReactInputMask from 'react-input-mask'
import { theme } from 'src/styles/themes/default'
import { PasswordClassification } from 'src/util/enums/password'
import styled from '@emotion/styled'
import { css } from '@emotion/react'

interface InputProps {
  hasError: boolean
}

interface PasswordStrengthMeterProgressProps {
  strength: PasswordClassification
}

interface SpanContentProps {
  hasError: boolean
}

const getStrength = (type: PasswordClassification) => {
  switch (type) {
    case PasswordClassification.WEAK:
      return css`
        background-color: #de1818;
      `
    case PasswordClassification.MEDIUM:
      return css`
        background-color: #ffe066;
      `
    case PasswordClassification.STRONG:
      return css`
        background-color: #247ba0;
      `
    case PasswordClassification.SAFE:
      return css`
        background-color: #70c1b3;
      `
    default:
      return css``
  }
}

export const CheckboxContainer = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  padding-top: 0.5rem;
`

export const CheckboxContent = styled.div`
  align-items: center;
  display: flex;
  margin-left: 2.875rem;

  &:first-child {
    margin-left: 0rem;
  }
  @media (max-width: 16.75rem) {
    margin-left: 0;
    :not(:first-child) {
      margin-top: 0.5rem;
    }
  }
`

export const CheckboxInput = styled.input`
  border-radius: 0.375rem;
  border: 0.0625rem solid #dcdde3;
  color: ${theme.colors.grey[300]};
  font-size: 0.875rem;
  height: 0.9375rem;
  margin-right: 0.5rem;
  width: 0.9375rem;

  &:checked {
    background: #3790ff;
    border: 0.0625rem solid #3790ff;
  }
`

export const FlexColumn = styled.div`
  align-items: start !important;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`

export const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;

  @media (max-width: 16.75rem) {
    align-items: start !important;
    flex-direction: column;
  }
`

export const GInputContent = styled.input<InputProps>`
  border: ${({ hasError }) =>
    hasError ? '.0625rem solid #bc3838' : '.0625rem solid #dcdde3'};
`

export const GSelectContent = styled.select<InputProps>`
  border: ${({ hasError }) =>
    hasError ? '.0625rem solid #bc3838' : '.0625rem solid #dcdde3'};
`

export const GRadioInputContainer = styled.div`
  align-items: center;
  display: flex;
  margin-left: 2.875rem;

  &:first-child {
    margin-left: 0rem;
  }
`

export const GRadioInputContent = styled.input`
  border-radius: 0.375rem;
  border: 0.0625rem solid #dcdde3;
  color: ${theme.colors.grey[300]};
  font-size: 0.875rem;
  height: 0.9375rem;
  margin-right: 0.5rem;
  width: 0.9375rem;

  &:checked {
    background-color: #3690ff;
    border-color: #3690ff;
  }
`

export const GRadioInputLabel = styled.label`
  color: ${theme.colors.grey[300]};
  font-size: 1rem;
  line-height: 1.1875rem;
  margin-bottom: 0rem;
`

export const ImageContent = styled.img`
  cursor: pointer;
  position: absolute;
  right: 2rem;
  top: 2.875rem;
`

export const InputContent = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
`

export const InputLoadingIcon = styled.div`
  position: absolute !important;
  right: 0.625rem;
  top: 2.8125rem;
  i {
    animation: around 5.4s infinite;
    display: inline-block;
    height: 1.25rem;
    position: relative;
    width: 1.25rem;
  }

  @keyframes around {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  i::after,
  i::before {
    content: '';
    animation: around 0.7s ease-in-out infinite;
    background: white;
    border-color: #0d71f0 #0d71f0 transparent transparent;
    border-radius: 1.25rem;
    border-style: solid;
    border-width: 0.125rem;
    box-sizing: border-box;
    display: inline-block;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  i::after {
    animation: around 0.7s ease-in-out 0.1s infinite;
    background: transparent;
  }
`

export const InputMaskContent = styled(ReactInputMask)<InputProps>`
  border: ${({ hasError }) =>
    hasError ? '.0625rem solid #bc3838' : '.0625rem solid #dcdde3'};
`

export const Label = styled.label`
  color: ${theme.colors.grey[300]};
  font-size: 1rem;
  line-height: 1.1875rem;
  margin-bottom: 0rem;
`

export const PasswordStrengthMeterContainer = styled.div`
  align-items: center;
  display: flex;
  margin-top: 1.5rem;
  text-align: left;
`

export const PasswordStrengthMeterLabel = styled.label`
  color: #a0a4b3 !important;
  font-size: 0.875rem !important;
  font-weight: 500;
  margin-bottom: 0rem !important;
  margin-left: 1.4375rem;
`

export const PasswordStrengthMeterProgress = styled.progress<PasswordStrengthMeterProgressProps>`
  appearance: none;
  height: 0.5rem;
  width: 15.625rem;
  -webkit-appearance: none;

  &::-webkit-progress-value {
    ${({ strength }) => getStrength(strength)}
    background-size: 2.1875rem 1.25rem, 100% 100%, 100% 100%;
    border-radius: 0.125rem;
  }

  &::-webkit-progress-bar {
    background-color: #eee;
    border-radius: 0.1875rem;
  }
`

export const SpanContent = styled.span<SpanContentProps>`
  ${({ hasError }) =>
    hasError &&
    css`
      color: #bc3838;
      font: normal 500 0.75rem Inter;
    `}
`
